import React, { createContext, useContext, useState, useEffect } from 'react';
import { onAuthStateChanged, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { doc, getDoc, setDoc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { canAccess as checkAccess, canAccessPath, normalizeUserPermissions } from '../utils/permissions';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let profileUnsub = null;
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      try {
        if (profileUnsub) {
          profileUnsub();
          profileUnsub = null;
        }
        if (firebaseUser) {
          setUser(firebaseUser);
          const userDocRef = doc(db, 'users', firebaseUser.uid);
          const userDocSnap = await getDoc(userDocRef);
          
          if (!userDocSnap.exists()) {
            const newUserData = {
              email: firebaseUser.email,
              displayName: 'Admin',
              role: 'admin',
              createdAt: serverTimestamp(),
              isActive: true,
              invoiceCount: 0
            };
            await setDoc(userDocRef, newUserData);
          }

          profileUnsub = onSnapshot(userDocRef, (snap) => {
            if (snap.exists()) {
              setUserData({ id: snap.id, ...snap.data() });
            }
          });
        } else {
          setUser(null);
          setUserData(null);
        }
      } catch (error) {
        console.error('Auth error:', error);
      }
      setLoading(false);
    });
    return () => {
      if (profileUnsub) profileUnsub();
      unsubscribe();
    };
  }, []);

  const login = async (email, password) => {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      return { success: true, user: result.user };
    } catch (error) {
      let message = 'Khalad ayaa dhacay';
      if (error.code === 'auth/user-not-found') message = 'Email-kan lama helin';
      if (error.code === 'auth/wrong-password') message = 'Password-ka waa khalad';
      if (error.code === 'auth/invalid-credential') message = 'Email ama Password waa khalad';
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const canAccess = (key) => checkAccess(userData, key);
  const permissions = normalizeUserPermissions(userData);

  return (
    <AuthContext.Provider value={{
      user,
      userData,
      permissions,
      loading,
      login,
      logout,
      isAuthenticated: !!user,
      canAccess,
      canAccessPath: (pathname) => canAccessPath(userData, pathname)
    }}>
      {children}
    </AuthContext.Provider>
  );
};